import React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Col, Row, Modal, Table, Button } from "react-bootstrap";

import {
  getStudentsForClass,
  getAssessmentTypes,
  assignAssessment,
  getStudents,
  updateUserActivity,
  getModalContentForCountry,
  getActiveGradeLevel3Schools,
  getMyTrialScreenerAccess,
} from "../services/APIClient";

import AssessmentTable from "./AssessmentTable";
import SelectClassStudents from "./SelectClassStudents";
import GradeCard from "./GradeCard";
import ReturnToAssessmentOverview from "./ReturnToAssessmentOverview";
import "./AssignAssessmentOverview.css";

const VideoModal = React.lazy(() =>
  import("../components/VideoModal")
);

function AssignAssessmentOverview(props) {
  const location = useLocation();
  const navigate = useNavigate();

  const classDetails = location.state.classDetails.classDetails;
  const name = location.state.name.name;
  const teachers = location.state.teachers.teachers;
  const gradeLevel = location.state.gradeLevel.gradeLevel;
  const schoolid = location.state.schoolid.schoolid;
  const gradeLevelOptions = location.state.gradeLevelOptions.gradeLevelOptions;
  let countryId = props.countryId;

  const classid = location.state.classDetails.classDetails.id;
  const teacherid = classDetails.teacherId;
  const isJIOnlyTrial = props.isJIOnlyTrial ?? false;
  const isPilot = props.isPilot ?? false;
  const latestPurchase = props.latestPurchase ?? null;
  const purchaseAccessError = props.purchaseAccessError ?? false;

  const [studentsResponse, setStudentsResponse] = useState("");
  const [classStudents, setClassStudents] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [selectStudentId, setSelectStudentId] = useState([]);
  const [selectAssessmentId, setSelectAssessmentId] = useState(null);
  const [selectAssessmentName, setSelectAssessmentName] = useState(null);
  const [isCheck, setIsCheck] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const [videoContent, setVideoContent] = useState(null);
  const [videoContentLoading, setVideoContentLoading] = useState(false);
  const [gradeLevel3Schools, setGradeLevel3Schools] = useState([]);

  const gradeLevel1 = classDetails.grade_level1;
  const gradeLevel2 = classDetails.grade_level2;
  const gradeLevel3 = classDetails.grade_level3;
  const gradeLevel4 = classDetails.grade_level4;
  const trialClass = classDetails.trial_class;

  // Country IDs
  const englandId = "74bb2897-dbbe-41e3-b547-caee866545cf";
  const usaId = "ab40949f-001e-48b9-848e-126decad8471";
  const uaeId = "1cb16a5c-7c0a-4e7a-bcbc-b409024dd6cf";
  const malaysiaId = "f2d8569e-47ca-4e88-8736-797a7acd37f3";

  // Transform API response to VideoModal format
  const transformApiDataToVideoContent = (apiData, countryId) => {
    const videos = apiData.flatMap(folder => 
      folder.cards.map(card => ({
        title: card.title,
        iframe: card.iframe,
        pdf_title: card.documentUrl ? `${card.title} - PDF Guide` : undefined,
        pdf: card.documentUrl
      }))
    );

    const staticText = getStaticTextForCountry(countryId);
    
    return {
      ...staticText,
      videos
    };
  };

  // Extract static text based on country
  const getStaticTextForCountry = (countryId) => {
    if (countryId === englandId || countryId === malaysiaId) {
      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and EAL",
        modalTitle: "Before you start screening your pupils...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
      };
    } else if (countryId === usaId) {
      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and ESL",
        modalTitle: "Before you start screening your students...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
      };
    } else if (countryId === uaeId) {
      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and EAL",
        modalTitle: "Before you start screening your students...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
      };
    } else {
      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and EAL",
        modalTitle: "Before you start screening your pupils...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
      };
    }
  };

  // Fallback function with dynamic gradeLevel3 support
 const getVideoContent = () => {
    if (countryId === englandId || countryId === malaysiaId) {
      // UK content (no changes)
      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and EAL",
        modalTitle: "Before you start screening your pupils...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
        videos: [
          {
            title: "Reception Midpoint Screener Walkthrough",
            iframe: "https://www.youtube.com/embed/lredqPra3Gw?si=HVNBQQgTURuauhOQ",
            pdf_title: "Reception Midpoint Word & Image List - PDF Guide",
            pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/UK%20Reception%20Midpoint%20-%20Word%20&%20Image%20list.pdf"
          },
          {
            title: "Year 1 Midpoint Screener Walkthrough",
            iframe: "https://www.youtube.com/embed/ixQ1zmZh2xM?si=hA77JRkm4tT8WsAX",
            pdf_title: "Year 1 Midpoint Word & Image List - PDF Guide",
            pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/UK%20Year%201%20Midpoint%20-%20Word%20&%20Image%20list.pdf"
          }
        ]
      };
    } else if (countryId === usaId) {
      // USA content (no changes)
      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and ESL",
        modalTitle: "Before you start screening your students...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
        videos: [
          {
            title: "Pre-K Midpoint Screener Walkthrough",
            iframe: "https://www.youtube.com/embed/Wze4acpFG5M?si=R9Qgu65LZgW_T4Z_",
            pdf_title: "Pre-K Midpoint Word & Image List - PDF Guide",
            pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/SandBox%20Content/Additional%20Resources/GradeLevel1%20Word%20&%20Image%20list%20-%20Midpoint.pdf"
          },
          {
            title: "Kindergarten Midpoint Screener Walkthrough",
            iframe: "https://www.youtube.com/embed/04gpHzRb-q4?si=mrHMYb6uiczXrydM",
            pdf_title: "Kindergarten Midpoint Word & Image List - PDF Guide",
            pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/SandBox%20Content/Additional%20Resources/GradeLevel2%20Word%20&%20Image%20list%20-%20Midpoint.pdf"
          }
        ]
      };
    } else if (countryId === uaeId) {
      // UAE content (no changes)
      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and EAL",
        modalTitle: "Before you start screening your students...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
        videos: [
          {
            title: "KG1 Midpoint Screener Walkthrough",
            iframe: "https://www.youtube.com/embed/gxXt6iY079s?si=4sUUv02rKeeR4KUt",
            pdf_title: "KG1 Midpoint Word & Image List - PDF Guide",
            pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/KG1%20Midpoint%20-%20Word%20&%20Image%20list.pdf"
          },
          {
            title: "KG2 Midpoint Screener Walkthrough",
            iframe: "https://www.youtube.com/embed/Xx6EAPt1ymA?si=wlamoQMavemSw0Ia",
            pdf_title: "KG2 Midpoint Word & Image List - PDF Guide",
            pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/KG2%20Midpoint%20-%20Word%20&%20Image%20list.pdf"
          }
        ]
      };
    } else {
      // Default/Irish content - WITH conditional 1st Class addition
      const defaultVideos = [
        {
          title: "Junior Infant Midpoint Screener Walkthrough",
          iframe: "https://www.youtube.com/embed/Wze4acpFG5M?si=R9Qgu65LZgW_T4Z_",
          pdf_title: "Junior Infant Midpoint Word & Image List - PDF Guide",
          pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/SandBox%20Content/Additional%20Resources/GradeLevel1%20Word%20&%20Image%20list%20-%20Midpoint.pdf"

        },
        {
          title: "Senior Infant Midpoint Screener Walkthrough",
          iframe: "https://www.youtube.com/embed/04gpHzRb-q4?si=mrHMYb6uiczXrydM",
          pdf_title: "Senior Infant Midpoint Word & Image List - PDF Guide",
          pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/SandBox%20Content/Additional%20Resources/GradeLevel2%20Word%20&%20Image%20list%20-%20Midpoint.pdf"
        }
      ];

      // Add 1st Class content for allowed schools
      if (schoolid && gradeLevel3Schools.includes(schoolid)) {
        defaultVideos.push({
          title: "1st Class Baseline Screener Walkthrough",
          iframe: "https://www.youtube.com/embed/rWY8DzXDqk4?si=9MfTTu4TS0CIg_bg",
          pdf_title: "1st Class Baseline Word & Image List - PDF Guide",
          pdf: "https://26983596.fs1.hubspotusercontent-eu1.net/hubfs/26983596/1st%20Class%20Word%20%26%20Image%20list%20-%20Baseline.pdf"
        });
      }

      return {
        buttonText: "📺 Show these videos in class before screening and review word & image list for AEN and EAL",
        modalTitle: "Before you start screening your pupils...",
        modalParagraph: "Please make sure to show this video to your class before screening. If you do that, children will understand they are about to go play a game and they will realize there are different types of questions that are going to be asked. You can make it an interactive activity in the classroom where you play the video in front of whole class, pause it after each question and ask for volunteers to give the answer.",
        videos: defaultVideos
      };
    }
  };

  console.log("props in AssignAssessmentOverview:", props);
  console.log("classDetails:", classDetails);

  useEffect(() => {
    getStudentsForClass(classid)
      .then((response) => {
        const combinedStudents = [
          ...(response.additionalStudents || []),
          ...(response.students || []),
        ];
        setClassStudents(combinedStudents);
      })
      .catch((err) => setError("Failed to fetch students for class."));
  }, [classid]);

  useEffect(() => {
    getAssessmentTypes()
      .then((response) => {
        const filteredAssessments = response.filter(
          (assessment) => assessment.task !== true
        );
        setAssessments(filteredAssessments);
      })
      .catch(() => setError("Failed to fetch assessments."));
  }, []);

  // Fetch GradeLevel3 schools on mount
  useEffect(() => {
    const fetchGradeLevel3Schools = async () => {
      try {
        const activeSchools = await getActiveGradeLevel3Schools();
        setGradeLevel3Schools(activeSchools);
        console.log("Active GradeLevel3 schools:", activeSchools);
      } catch (error) {
        console.error("Error fetching GradeLevel3 schools:", error);
        setGradeLevel3Schools([]);
      }
    };

    fetchGradeLevel3Schools();
  }, []);

  // Fetch video content from API
  useEffect(() => {
    const fetchVideoContent = async () => {
      if (!countryId) return;
      
      setVideoContentLoading(true);
      
      try {
        console.log('Fetching video content for countryId:', countryId);
        const apiData = await getModalContentForCountry(countryId);
        console.log('API response:', apiData);
        
        if (apiData && apiData.length > 0) {
          const transformedContent = transformApiDataToVideoContent(apiData, countryId);
          console.log('Using API content:', transformedContent);
          setVideoContent(transformedContent);
        } else {
          console.warn('No API data returned, using fallback');
          setVideoContent(getVideoContent());
        }
      } catch (error) {
        console.error("Failed to fetch video content from API, using fallback:", error);
        setVideoContent(getVideoContent());
      } finally {
        setVideoContentLoading(false);
      }
    };

    fetchVideoContent();
  }, [countryId, schoolid, gradeLevel3Schools]);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const newSelectStudentId = selectStudentId.map((studentId) => ({
    id: studentId,
  }));

  const handleClick = async () => {
    if (assigning) return;
    setAssigning(true);
    setError(null);
    try {
      const trialAccess = await getMyTrialScreenerAccess();
      if (
        trialAccess.restricted &&
        !trialAccess.enabledAssessmentIds.includes(selectAssessmentId)
      ) {
        setSelectAssessmentId(null);
        setSelectAssessmentName(null);
        setShowModal(false);
        setError("That screener is no longer available. Please select another screener.");
        return;
      }
      await assignAssessment(
        newSelectStudentId,
        selectAssessmentId,
        selectAssessmentName
      );
      updateUserActivity(teacherid, "screener-assigned");
      navigate(-1, {
          state: { toast: { status: true, msg: "Assessments Assigned" } },
      });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.error?.message ||
        requestError?.response?.data?.message ||
        "The screener could not be assigned. A pupil may already have another active screener.";
      setShowModal(false);
      setError(message);
    } finally {
      setAssigning(false);
    }
  };

  const allAssigned = classStudents?.every(
    (student) =>
      student.disabled ||
      (student.assessments &&
        student.assessments.some((assessment) => !assessment.completed))
  );

  const getSelectedStudentInfo = () => {
    return classStudents
      ?.filter((student) => selectStudentId.includes(student.id))
      ?.map((student, index) => ({
        index: index + 1,
        name: student.name,
        assessment: selectAssessmentName || "N/A",
      }));
  };

  const selectedStudents = getSelectedStudentInfo();
  const noStudentsSelectedMessage = allAssigned
    ? "All students in the class have already been assigned a screener."
    : "No students have been selected to assign a screener.";

  console.log("classStudents:", classStudents);
  console.log("selectAssessmentId:", selectAssessmentId);

  return (
    <div className="class-overview-container">
      <Container className="container-shape">
        <Row className="page-description-bar">
          <Col className="col-5">
            <h2 className="page-description-header">Assign Screener</h2>
            <p>Assign screeners to the students in your class.</p>
          </Col>
          <Col className="col-2">
            <GradeCard
              gradeLevel={gradeLevel}
              gradeLevel1={gradeLevel1}
              gradeLevel2={gradeLevel2}
              gradeLevel3={gradeLevel3}
              gradeLevel4={gradeLevel4}
              trialClass={trialClass}
            />
          </Col>
          <Col className="col-5">
            <Row>
              <Col className="col-6"></Col>
              <Col className="col-6">
                <ReturnToAssessmentOverview
                  classDetails={classDetails}
                  teachers={teachers}
                  name={name}
                  gradeLevel={gradeLevel}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <hr />
        {error && <p className="text-danger">{error}</p>}
        {videoContent && !videoContentLoading && (
          <VideoModal
            buttonText={videoContent.buttonText}
            modalTitle={videoContent.modalTitle}
            modalParagraph={videoContent.modalParagraph}
            videos={videoContent.videos}
          />
        )}
        <Row>
          <Col className="col-7 vertical-border-line">
            <SelectClassStudents
              students={studentsResponse.students}
              classStudents={classStudents}
              selectStudentId={selectStudentId}
              setSelectStudentId={setSelectStudentId}
              isCheck={isCheck}
              setIsCheck={setIsCheck}
              name={name}
              selectAssessmentId={selectAssessmentId}
              gradeLevel1={gradeLevel1}
              gradeLevel2={gradeLevel2}
              gradeLevel3={gradeLevel3}
              gradeLevel4={gradeLevel4}
              availableAssessments={availableAssessments}
            />
          </Col>
          <Col className="col-5">
            <AssessmentTable
              assessments={assessments}
              selectAssessmentId={selectAssessmentId}
              setSelectAssessmentId={setSelectAssessmentId}
              setSelectAssessmentName={setSelectAssessmentName}
              classDetails={classDetails}
              gradeLevelOptions={gradeLevelOptions}
              schoolid={schoolid}
              countryId={countryId}
              onAssessmentsLoaded={setAvailableAssessments}
              isJIOnlyTrial={isJIOnlyTrial}
              isPilot={isPilot}
              latestPurchase={latestPurchase}
              purchaseAccessError={purchaseAccessError}
            />
          </Col>
          <Col>
            <div className="button-placer">
              {selectAssessmentId && (
                <button
                  onClick={handleShowModal}
                  className="blue-button save-button"
                >
                  Assign
                </button>
              )}
            </div>
          </Col>
        </Row>

        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Confirm Screener Assignment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedStudents?.length === 0 ? (
              <p>{noStudentsSelectedMessage}</p>
            ) : (
              <>
                <p>The following students will receive the screener:</p>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student Name</th>
                      <th>Screener Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStudents?.map((student) => (
                      <tr key={student.index}>
                        <td>{student.index}</td>
                        <td>{student.name}</td>
                        <td>{student.assessment}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="white-button" onClick={handleCloseModal}>
              Close
            </button>
            {selectedStudents?.length > 0 && (
              <button
                className="green-button"
                blue-button
                onClick={handleClick}
                disabled={assigning}
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default AssignAssessmentOverview;
